import { Trans } from "@lingui/react/macro";
import React from "react";
import {
  Button,
  FareInfographicsMosaic,
  InclusionContent
} from "@silversea-ssc/ui-components-react";
import type { InclusionCode } from "@silversea-ssc/web-inclusions";
import {
  FareMosaicCardFragment,
  GenericInfographicStepInclusionFragment,
  InfographicCardInclusionContentFragment
} from "types/graphql-types";
import { IconsId } from "@silversea-ssc/design-system-icons";
import { InclusionsDrawerProvider } from "components/fare/InclusionsDrawer/InclusionsDrawerProvider";
import { extractFareCardHeaderContent } from "components/fare/FareCard/utils";
import { FareCode } from "@silversea-ssc/web-fares";
import MiniFareCard from "../FareCard/MiniFareCard";
import { isD2DFareCode } from "src/utils/fares";

interface StaticMiniFaresMosaicProps {
  fares: FareMosaicCardFragment[];
  inclusionsContent: (InfographicCardInclusionContentFragment &
    GenericInfographicStepInclusionFragment)[];
}

const toInfographicContent = (
  content: InfographicCardInclusionContentFragment
): InclusionContent => {
  return {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    _id: content._id!,
    icon: content.icon as IconsId,
    codes: content.codes as InclusionCode[],
    infographicTitle: content.genericContent?.infographicTitle?.localized || "",
    title: content.genericContent?.title?.localized,
    description: content.genericContent?.description?.localized,
    footnote: content.genericContent?.footnote?.localized
  };
};

const StaticMiniFaresMosaic: React.FC<
  React.PropsWithChildren<StaticMiniFaresMosaicProps>
> = ({ fares, inclusionsContent }) => {
  return (
    <InclusionsDrawerProvider
      inclusionsContent={inclusionsContent.map(toInfographicContent)}
    >
      <FareInfographicsMosaic>
        {fares.map((fare) => {
          const { enabledInclusions, _id } = fare;
          const fareTitle = fare?.card?.title?.localized;
          const fareCode = fare.fareCode as FareCode;
          return (
            <MiniFareCard
              {...extractFareCardHeaderContent(fare)}
              key={_id}
              hilighted={isD2DFareCode(fareCode)}
              fareCode={fareCode}
              enabledInclusions={enabledInclusions as InclusionCode[]}
              footerNotesForNonAirCountries={
                fare.infographicFooterNotesForNonAirCountries?.localized
              }
              ctaButton={
                fare.fullPath ? (
                  <Button
                    variant="inline-text"
                    href={fare.fullPath}
                    dataContentPiece="fare_details"
                  >
                    <Trans>{fareTitle} details</Trans>
                  </Button>
                ) : undefined
              }
            />
          );
        })}
      </FareInfographicsMosaic>
    </InclusionsDrawerProvider>
  );
};

export default StaticMiniFaresMosaic;
